import React, { useState } from 'react';
import { BarChart3, BookOpen, FileText } from 'lucide-react';
import { Header } from './components/Header';
import { QueueingAnalysisTab } from './components/QueueingAnalysisTab';
import { TheoryTab } from './components/TheoryTab';
import { ReadmeTab } from './components/ReadmeTab';

type TabType = 'analysis' | 'theory' | 'readme';

function App() {
  const [activeTab, setActiveTab] = useState<TabType>('analysis');
  const [isCalculating, setIsCalculating] = useState(false);

  const tabs = [
    {
      id: 'analysis' as TabType,
      label: 'Queueing Analysis',
      icon: <BarChart3 className="w-4 h-4" />,
      description: 'Interactive analysis and visualization'
    },
    {
      id: 'theory' as TabType,
      label: 'Theory',
      icon: <BookOpen className="w-4 h-4" />,
      description: 'Mathematical foundations and concepts'
    },
    {
      id: 'readme' as TabType,
      label: 'README',
      icon: <FileText className="w-4 h-4" />,
      description: 'Platform overview and documentation'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'analysis':
        return <QueueingAnalysisTab isCalculating={isCalculating} setIsCalculating={setIsCalculating} />;
      case 'theory':
        return <TheoryTab />;
      case 'readme':
        return <ReadmeTab />;
      default:
        return <QueueingAnalysisTab isCalculating={isCalculating} setIsCalculating={setIsCalculating} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <Header 
        isCalculating={isCalculating}
      />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="flex flex-wrap border-b border-dark-600">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center space-x-2 px-6 py-3 font-medium text-sm transition-all duration-200
                  border-b-2 mr-2 mb-0 rounded-t-lg
                  ${
                    activeTab === tab.id
                      ? 'text-primary-400 border-primary-400 bg-dark-800'
                      : 'text-gray-400 border-transparent hover:text-gray-300 hover:border-gray-600 hover:bg-dark-900'
                  }
                `}
              >
                {tab.icon}
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
          
          {/* Tab descriptions */}
          <div className="mt-4 px-1">
            <p className="text-gray-400 text-sm">
              {tabs.find(tab => tab.id === activeTab)?.description}
            </p>
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {renderTabContent()}
        </div>

        {/* Loading Overlay - Only show for analysis tab */}
        {isCalculating && activeTab === 'analysis' && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-dark-800 rounded-xl p-8 border border-dark-600 flex items-center space-x-4">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              <div>
                <h3 className="font-medium text-gray-200">Calculating System Metrics</h3>
                <p className="text-sm text-gray-400">Analyzing queueing performance...</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;